<template>
  <simple-dialog ref="dlg" :title="title" width="650" :close-on-save="false" @ok="save">
    <template v-slot:content>
      <v-autocomplete
        label="Organizations (includes hierarchy)"
        v-model="localFilters.organizationIds"
        :items="organizations"
        item-title="name"
        item-value="organizationId"
        multiple
        chips
        closable-chips
        class="mb-3"
      />
      <v-autocomplete
        label="Sections"
        v-model="localFilters.sectionIds"
        :items="sections"
        item-title="description"
        item-value="generalID"
        multiple
        chips
        closable-chips
        class="mb-3"
      />
      <v-autocomplete
        label="Ranks"
        v-model="localFilters.rankIds"
        :items="ranks"
        item-title="description"
        item-value="generalID"
        multiple
        chips
        closable-chips
        class="mb-3"
      />
      <div class="text-subtitle-2 mb-2">Gender</div>
      <v-radio-group v-model="localFilters.gender" inline density="compact" class="mb-3">
        <v-radio label="All" value="All" />
        <v-radio label="M" value="M" />
        <v-radio label="F" value="F" />
      </v-radio-group>
      <v-row>
        <v-col cols="6">
          <v-text-field label="Age from" v-model.number="localFilters.ageFrom" type="number" min="0" hide-details />
        </v-col>
        <v-col cols="6">
          <v-text-field label="Age to" v-model.number="localFilters.ageTo" type="number" min="0" hide-details />
        </v-col>
      </v-row>
    </template>
  </simple-dialog>
</template>

<script setup>
import { ref, watch, onMounted, inject } from "vue";
import { getAllOrganizations, getSections, getGeneralsById } from "@/services/utilitiesService";
import { updateMiqaatFilters } from "@/services/miqaatService";
import { emptySurveyFilters, filtersToRows, rowsToFilters } from "@/views/SurveyForms/utils/surveyFilterUtils";

const props = defineProps({
  modelValue: { type: Object, default: () => emptySurveyFilters() },
  title: { type: String, default: "Miqaat Filters" },
  miqaatId: { type: Number, default: null },
});

const emit = defineEmits(["update:modelValue", "saved"]);

const loader = inject("loader");
const snackbar = inject("snackBar");

const dlg = ref(null);
const localFilters = ref(emptySurveyFilters());
const organizations = ref([]);
const sections = ref([]);
const ranks = ref([]);

watch(
  () => props.modelValue,
  (val) => {
    localFilters.value = { ...emptySurveyFilters(), ...(val || {}) };
  },
  { immediate: true, deep: true }
);

onMounted(async () => {
  const [orgs, secs, rnk] = await Promise.all([
    getAllOrganizations().catch(() => []),
    getSections().catch(() => []),
    getGeneralsById(9).catch(() => []),
  ]);
  organizations.value = orgs;
  sections.value = secs;
  ranks.value = rnk;
});

function show() {
  localFilters.value = { ...emptySurveyFilters(), ...(props.modelValue || {}) };
  dlg.value.show();
}

async function save() {
  const value = { ...localFilters.value };
  emit("update:modelValue", value);

  if (!props.miqaatId) {
    dlg.value.hide();
    return;
  }

  loader.show();
  try {
    const result = await updateMiqaatFilters(props.miqaatId, filtersToRows(value));
    emit("update:modelValue", rowsToFilters(result.filters));
    emit("saved");
    snackbar.success("Filters saved");
    dlg.value.hide();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>
